import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Search, Flame, Sparkles, MapPin, ShoppingBag, Award, Newspaper,
  Heart, ShieldCheck, Globe, Users, BookmarkCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SaveToBoardDialog from "@/components/SaveToBoardDialog";
import BlinterestSection from "@/components/blinterest/BlinterestSection";
import LatestDropsRail from "@/components/LatestDropsRail";
import {
  ProductCard, BusinessCard, FoodExperienceCard, ShopBlackCard,
  CategoryTile, MomentCard,
} from "@/components/blinterest/BlinterestCards";
import BusinessLogo from "@/components/BusinessLogo";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * Blinterest V2 — curated discovery homepage.
 *
 * Architecture:
 *  - The old masonry feed lives at /blinterest/discover (unchanged).
 *  - This page is / blinterest — a section-based, mobile-first ecosystem.
 *  - Each section fetches its own aggregator endpoint in parallel; a slow
 *    endpoint doesn't block the rest.
 *  - The right-hand sidebar (Community Spotlight, Top Categories, Near You)
 *    is desktop-only and hidden behind lg:.
 */
export default function BlinterestHomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [category, setCategory] = useState(searchParams.get("cat") || "for-you");
  const [pendingSearch, setPendingSearch] = useState("");

  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [explore, setExplore] = useState([]);
  const [trending, setTrending] = useState([]);
  const [madeForYou, setMadeForYou] = useState([]);
  const [shopBlack, setShopBlack] = useState([]);
  const [nearYou, setNearYou] = useState([]);
  const [businessesToKnow, setBusinessesToKnow] = useState([]);
  const [moments, setMoments] = useState([]);
  const [justAdded, setJustAdded] = useState([]);
  const [communityFavs, setCommunityFavs] = useState([]);
  const [trendingBoards, setTrendingBoards] = useState([]);
  // Admin-controlled editorial toggles — hide / show homepage rails
  // without a redeploy. Defaults to "show" so a network hiccup on the
  // settings endpoint never blanks the page.
  const [editorial, setEditorial] = useState({
    blinterest_show_featured_collections: true,
    blinterest_show_shop_the_moment: true,
    blinterest_show_just_added: true,
  });

  const [saveDialogPin, setSaveDialogPin] = useState(null);

  // Fetch all sections in parallel — each is best-effort and silently
  // empties on failure so a single dead endpoint doesn't blank the page.
  useEffect(() => {
    const get = (path) => axios.get(`${API}${path}`, { withCredentials: true })
      .then((r) => r.data).catch(() => null);

    (async () => {
      const [
        cats, pops, exp, trend, madeFor, shop, near, biz,
        moms, added, favs, boards,
      ] = await Promise.all([
        get("/blinterest/categories"),
        get("/blinterest/discover/popular-searches"),
        get("/blinterest/discover/explore-categories"),
        get("/blinterest/discover/trending?limit=12"),
        user ? get("/blinterest/personalised?limit=10") : Promise.resolve(null),
        get("/blinterest/discover/shop-black?limit=12"),
        get("/blinterest/discover/near-you?limit=8"),
        get("/blinterest/discover/businesses-to-know?limit=10"),
        get("/blinterest/discover/moments"),
        get("/blinterest/discover/just-added?limit=10"),
        get("/blinterest/discover/community-favorites?limit=10"),
        get("/blinterest/boards/trending?limit=5"),
      ]);
      setCategories(cats?.categories || []);
      setPopular(pops?.searches || []);
      setExplore(exp?.categories || []);
      setTrending(trend?.pins || []);
      setMadeForYou(madeFor?.pins || []);
      setShopBlack(shop?.products || []);
      setNearYou(near?.businesses || []);
      setBusinessesToKnow(biz?.businesses || []);
      setMoments(moms?.moments || []);
      setJustAdded(added?.pins || []);
      setCommunityFavs(favs?.pins || []);
      setTrendingBoards(boards?.boards || []);
    })();
  }, [user]);

  // Load admin-controlled editorial toggles once. Public endpoint —
  // no auth required. Failure keeps defaults ("show all").
  useEffect(() => {
    axios.get(`${API}/public/settings/homepage`, { withCredentials: false })
      .then((r) => setEditorial((prev) => ({ ...prev, ...(r.data || {}) })))
      .catch(() => {});
  }, []);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = pendingSearch.trim();
    navigate(`/blinterest/discover${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  const onCategoryChip = (slug) => {
    setCategory(slug);
    navigate(`/blinterest/discover?cat=${slug}`);
  };

  const handleSave = (pin) => {
    if (!user) {
      navigate("/join?returnTo=/blinterest");
      return;
    }
    setSaveDialogPin(pin);
  };

  const spotlightBusiness = useMemo(
    () => businessesToKnow[0] || nearYou[0] || null,
    [businessesToKnow, nearYou],
  );

  return (
    <div className="min-h-screen bg-tbc-navy pb-24 lg:pb-0" data-testid="blinterest-home-page">
      <Navbar />

      {/* ==================== HERO ==================== */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-12 pb-6 lg:pb-10">
          <div className="text-center">
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-gold tracking-tight"
              data-testid="blinterest-title"
            >
              BLINTEREST
            </h1>
            <p className="mt-2 text-white/70 font-body text-sm sm:text-base">
              by The BLK Community
            </p>
            <p className="mt-1 text-tbc-gold/80 font-body text-xs sm:text-sm italic">
              Find what you love. Support who made it.
            </p>

            <form
              onSubmit={onSearchSubmit}
              className="mt-6 max-w-2xl mx-auto flex gap-2 px-2"
              data-testid="blinterest-search-form"
            >
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={pendingSearch}
                  onChange={(e) => setPendingSearch(e.target.value)}
                  placeholder="Search Blinterest — products, businesses, ideas…"
                  className="pl-10 bg-white text-tbc-navy border-0 h-11 rounded-full"
                  data-testid="blinterest-search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy h-11 px-5 sm:px-6 rounded-full"
                data-testid="blinterest-search-submit"
              >
                Search
              </Button>
            </form>

            {popular.length > 0 && (
              <div className="mt-4 flex flex-wrap justify-center gap-2 px-2">
                <span className="text-white/50 text-xs sm:text-sm mr-1">
                  Popular searches:
                </span>
                {popular.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => navigate(`/blinterest/discover?q=${encodeURIComponent(s)}`)}
                    className="px-3 py-1 rounded-full border border-white/25 text-white/80 hover:text-tbc-navy hover:bg-tbc-gold hover:border-tbc-gold text-xs sm:text-sm transition-colors"
                    data-testid={`popular-search-${s.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category chips — sticky-lite under the hero */}
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 overflow-x-auto scrollbar-hidden">
            <div className="flex gap-2 min-w-max">
              <button
                type="button"
                onClick={() => onCategoryChip("for-you")}
                className={`px-4 py-1.5 rounded-full text-sm font-body whitespace-nowrap border transition-colors ${
                  category === "for-you"
                    ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
                    : "bg-transparent text-white/80 border-white/25 hover:border-tbc-gold/60"
                }`}
                data-testid="cat-chip-for-you"
              >
                For You
              </button>
              {categories.filter((c) => c.slug !== "for-you").map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => onCategoryChip(c.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-body whitespace-nowrap border transition-colors ${
                    category === c.slug
                      ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
                      : "bg-transparent text-white/80 border-white/25 hover:border-tbc-gold/60"
                  }`}
                  data-testid={`cat-chip-${c.slug}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== LATEST DROPS RAIL ==================== */}
      {/* Hero-level community drops rail — surfaces the freshest published
          drops across every business so Blinterest visitors immediately
          see live activity. Renders null when there's nothing to show. */}
      <LatestDropsRail placement="blinterest" limit={12} />

      {/* ==================== MAIN + SIDEBAR ==================== */}
      <div className="max-w-7xl mx-auto lg:px-8 py-6 lg:py-10 grid lg:grid-cols-4 gap-8">
        {/* -------- MAIN COLUMN -------- */}
        <div className="lg:col-span-3 min-w-0">
          {/* Explore Black-Owned */}
          {explore.length > 0 && (
            <BlinterestSection
              title="Explore Black-Owned"
              seeAllHref="/directory"
              testid="section-explore"
            >
              {explore.map((c) => (
                <div key={c.slug} className="w-32 sm:w-36 flex-none snap-start">
                  <CategoryTile cat={c} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Trending */}
          {trending.length > 0 && (
            <BlinterestSection
              title="Trending on Blinterest"
              icon={<Flame className="w-4 h-4" />}
              seeAllHref="/blinterest/discover?cat=for-you"
              testid="section-trending"
            >
              {trending.map((p) => (
                <div key={p.pin_id} className="w-40 sm:w-44 flex-none snap-start">
                  <ProductCard pin={p} onSave={handleSave} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Shop Black — real products (only if we have any). Elevated
              above Made For You because Blinterest is the new marketplace. */}
          {shopBlack.length > 0 && (
            <BlinterestSection
              title="Shop Black"
              icon={<ShoppingBag className="w-4 h-4" />}
              seeAllHref="/directory?tab=products"
              testid="section-shop-black"
            >
              {shopBlack.map((p) => (
                <div key={p.product_id} className="w-40 sm:w-44 flex-none snap-start">
                  <ShopBlackCard product={p} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Made For You */}
          {madeForYou.length > 0 && (
            <BlinterestSection
              title="Made For You"
              icon={<Sparkles className="w-4 h-4" />}
              seeAllHref="/blinterest/discover"
              testid="section-made-for-you"
            >
              {madeForYou.map((p) => (
                <div key={p.pin_id} className="w-40 sm:w-44 flex-none snap-start">
                  <ProductCard pin={p} onSave={handleSave} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Near You */}
          {nearYou.length > 0 && (
            <BlinterestSection
              title="Near You"
              icon={<MapPin className="w-4 h-4" />}
              seeAllHref="/directory"
              testid="section-near-you"
            >
              {nearYou.map((b) => (
                <div key={b.business_id} className="w-40 sm:w-44 flex-none snap-start">
                  <FoodExperienceCard business={b} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Businesses to Know — treated as a Featured Collections rail
              from the admin editorial perspective; toggle-gated. */}
          {editorial.blinterest_show_featured_collections && businessesToKnow.length > 0 && (
            <BlinterestSection
              title="Businesses to Know"
              icon={<Award className="w-4 h-4" />}
              seeAllHref="/directory?verified=1"
              testid="section-businesses-to-know"
            >
              {businessesToKnow.map((b) => (
                <div key={b.business_id} className="w-36 sm:w-40 flex-none snap-start">
                  <BusinessCard business={b} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Shop the Moment */}
          {editorial.blinterest_show_shop_the_moment && moments.length > 0 && (
            <BlinterestSection
              title="Shop the Moment"
              seeAllHref="/blinterest/discover"
              testid="section-moments"
            >
              {moments.map((m) => (
                <div key={m.slug} className="w-40 sm:w-44 flex-none snap-start">
                  <MomentCard moment={m} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Just Added */}
          {editorial.blinterest_show_just_added && justAdded.length > 0 && (
            <BlinterestSection
              title="Just Added"
              icon={<Newspaper className="w-4 h-4" />}
              seeAllHref="/blinterest/discover"
              testid="section-just-added"
            >
              {justAdded.map((p) => (
                <div key={p.pin_id} className="w-40 sm:w-44 flex-none snap-start relative">
                  <span
                    className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-tbc-gold text-tbc-navy text-[9px] font-heading tracking-widest uppercase"
                  >
                    New
                  </span>
                  <ProductCard pin={p} onSave={handleSave} />
                </div>
              ))}
            </BlinterestSection>
          )}

          {/* Community Favorites */}
          {communityFavs.length > 0 && (
            <BlinterestSection
              title="Community Favorites"
              icon={<Heart className="w-4 h-4" />}
              seeAllHref="/blinterest/discover"
              testid="section-community-favs"
            >
              {communityFavs.map((p) => (
                <div key={p.pin_id} className="w-40 sm:w-44 flex-none snap-start">
                  <ProductCard pin={p} onSave={handleSave} />
                </div>
              ))}
            </BlinterestSection>
          )}
        </div>

        {/* -------- SIDEBAR (desktop only) -------- */}
        <aside className="hidden lg:block space-y-4">
          {/* Shop Black. Build Black. */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <ShoppingBag className="w-8 h-8 text-tbc-gold mb-3" />
            <p className="font-heading text-white text-lg leading-tight">
              Shop Black. Build Black. Grow Together.
            </p>
            <p className="text-white/60 text-xs mt-2 leading-relaxed">
              Discover thousands of Black-owned businesses and products from
              around the world.
            </p>
            <Link to="/directory">
              <Button className="mt-3 w-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-9 text-sm">
                Learn More
              </Button>
            </Link>
          </div>

          {/* Community Spotlight */}
          {spotlightBusiness && (
            <div
              className="rounded-2xl border border-white/10 bg-white/5 p-4"
              data-testid="sidebar-spotlight"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/50 text-[10px] uppercase tracking-widest">
                  Community Spotlight
                </p>
                <Link to="/directory" className="text-tbc-gold text-xs hover:underline">
                  View all
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <BusinessLogo
                  name={spotlightBusiness.name}
                  logoUrl={spotlightBusiness.logo_url}
                  size="lg"
                  shape="rounded"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-white text-sm truncate">
                    {spotlightBusiness.name}
                  </p>
                  {(spotlightBusiness.city || spotlightBusiness.state) && (
                    <p className="text-white/50 text-xs truncate">
                      {[spotlightBusiness.city, spotlightBusiness.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </div>
              {spotlightBusiness.description && (
                <p className="text-white/60 text-xs mt-3 line-clamp-2">
                  {spotlightBusiness.description}
                </p>
              )}
              <Link to={`/business/${spotlightBusiness.business_id}`}>
                <Button
                  variant="outline"
                  className="mt-3 w-full border-tbc-gold/50 text-tbc-gold bg-transparent hover:bg-tbc-gold hover:text-tbc-navy rounded-full h-9 text-sm"
                >
                  View Business
                </Button>
              </Link>
            </div>
          )}

          {/* Top Categories */}
          {explore.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-white/50 text-[10px] uppercase tracking-widest mb-3">
                Top Categories
              </p>
              <ul className="space-y-2">
                {explore.slice(0, 5).map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/blinterest/discover?cat=${c.slug}`}
                      className="flex items-center gap-2 text-white/80 hover:text-tbc-gold text-sm py-1"
                      data-testid={`sidebar-cat-${c.slug}`}
                    >
                      <span className="text-tbc-gold">★</span>
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Near You mini rail */}
          {nearYou.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/50 text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Near You
                </p>
                <Link to="/directory" className="text-tbc-gold text-xs hover:underline">
                  View all
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {nearYou.slice(0, 4).map((b) => (
                  <Link
                    key={b.business_id}
                    to={`/business/${b.business_id}`}
                    className="block rounded-lg overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/50"
                    data-testid={`sidebar-near-${b.business_id}`}
                  >
                    <div className="aspect-square overflow-hidden">
                      {b.cover_image || b.logo_url ? (
                        <img
                          src={resolveAssetUrl(b.cover_image || b.logo_url)}
                          alt={b.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-tbc-gold/20" />
                      )}
                    </div>
                    <p className="text-white text-xs px-1.5 py-1 truncate">{b.name}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Trending Collections */}
          {trendingBoards.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-white/50 text-[10px] uppercase tracking-widest mb-3 inline-flex items-center gap-1">
                <BookmarkCheck className="w-3 h-3" />
                Trending Collections
              </p>
              <ul className="space-y-2">
                {trendingBoards.map((b) => (
                  <li key={b.board_id}>
                    <Link
                      to={`/blinterest/board/${b.board_id}`}
                      className="text-white/80 hover:text-tbc-gold text-sm block truncate"
                    >
                      {b.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* ==================== VALUE STRIP ==================== */}
      <section className="hidden lg:block bg-tbc-navy border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-5 gap-6">
            <ValuePill
              icon={<ShieldCheck className="w-5 h-5" />}
              title="Verified Black-Owned"
              body="Every business is verified so you can shop with confidence."
            />
            <ValuePill
              icon={<Heart className="w-5 h-5" />}
              title="Support & Empower"
              body="Every purchase helps build stronger communities."
            />
            <ValuePill
              icon={<Globe className="w-5 h-5" />}
              title="Worldwide Marketplace"
              body="Discover Black-owned businesses from around the world."
            />
            <ValuePill
              icon={<BookmarkCheck className="w-5 h-5" />}
              title="Save & Organize"
              body="Save your favorites and create collections you love."
            />
            <ValuePill
              icon={<Users className="w-5 h-5" />}
              title="Be Part of the Movement"
              body="Join a community that's building our economic future together."
            />
          </div>
        </div>
      </section>

      <Footer />

      <SaveToBoardDialog
        pin={saveDialogPin}
        open={Boolean(saveDialogPin)}
        onOpenChange={(o) => !o && setSaveDialogPin(null)}
      />
    </div>
  );
}

function ValuePill({ icon, title, body }) {
  return (
    <div className="text-center">
      <div className="w-10 h-10 mx-auto rounded-full bg-tbc-gold/15 text-tbc-gold flex items-center justify-center mb-2">
        {icon}
      </div>
      <p className="font-heading text-white text-sm mb-1">{title}</p>
      <p className="text-white/60 text-xs leading-relaxed">{body}</p>
    </div>
  );
}
